import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead

import { Subscribe } from 'unstated';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { getSuperAdminClaim } from '@confluence/super-admin-claim';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { LoadableLazy, useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import { AdminKeyQuery } from './AdminKeyQuery.js';
import { DisableSuperAdminMutation } from './disable-super-admin/DisableSuperAdminMutationComponent.tsx';

import { SuperAdminExpiration } from './super-admin-expiration/SuperAdminExpiration.js';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

const SuperAdminSatisfactionSurveyLoader = LoadableLazy<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SuperAdminSatisfactionSurvey" */ './SuperAdminSatisfactionSurvey.js'
			)
		).SuperAdminSatisfactionSurvey,
});

const BANNER_NAME = 'SuperAdminBanner';

const bannerWrapperStyles = xcss({
	width: '100%',
});

const BANNER_CONTENTS_HEIGHT = 8 * 3.5; // 28px when gridSize = 8px
const BANNER_FONT_SIZE = BANNER_CONTENTS_HEIGHT / 2; // 14px when gridSize = 8px
const BANNER_PADDING_SIZE = BANNER_FONT_SIZE - 8 / 4; // 12px when gridSize = 8px
const BANNER_TOTAL_HEIGHT = BANNER_CONTENTS_HEIGHT + BANNER_PADDING_SIZE * 2; // 52px when gridSize = 8px;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Banner = styled.div({
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${BANNER_CONTENTS_HEIGHT}px`,
	backgroundColor: `${token('color.background.warning.bold')}`,
	color: `${token('color.text.warning.inverse')}`,
	font: token('font.body'),

	padding: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerText = styled.span({
	padding: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-anchor -- To migrate as part of go/ui-styling-standard
const ExitLink = styled.a({
	color: 'inherit',
	cursor: 'pointer',
	fontWeight: token('font.weight.regular'),
	marginLeft: token('space.050'),
	textDecoration: 'underline',
});

const WARNING_ICON = (
	<StatusWarningIcon
		label="Warning icon"
		spacing="spacious"
		color={token('color.icon.warning.inverse')}
	/>
);

export const SuperAdminBannerComponent: FC<{
	bannerState: BannerStateContainer;
}> = ({ bannerState }) => {
	const [isSurveyVisible, setIsSurveyVisible] = useState<boolean>(false);
	const [bannerHeight, setBannerHeight] = useState<number>(BANNER_TOTAL_HEIGHT);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	useEffect(() => {
		const surveyIsVisible = localStorage.getItemAsBoolean(keys.SUPERADMIN_SURVEY_REQUESTED);
		if (surveyIsVisible) {
			setIsSurveyVisible(true);
		}
	}, []);

	/**
	 * Shows the Super Admin Satisfaction Survey if it isn't been shown to the user recently, and sets the
	 * persistent survey state which is needed because exiting Super Admin mode causes a full page reload,
	 * which would cause the normal state data to be lost.
	 * @param {boolean} showImmediately - set to true to show immediately, false to only show after next
	 *    remount (aka, wait until after the page reload so there's no flickering of the survey).
	 */
	const showSurvey = (showImmediately) => {
		const surveyRecentlySubmitted = localStorage.getItemAsBoolean(keys.SUPERADMIN_SURVEY_SUBMITTED);
		if (!surveyRecentlySubmitted) {
			localStorage.setItem(keys.SUPERADMIN_SURVEY_REQUESTED, 'true');
			if (showImmediately) {
				setIsSurveyVisible(true);
			}
		}
	};

	/**
	 * Hides the Super Admin Satisfaction Survey, and clears the persistent survey state
	 */
	const hideSurvey = () => {
		localStorage.removeItem(keys.SUPERADMIN_SURVEY_REQUESTED);
		setIsSurveyVisible(false);
	};

	const fireAdminKeyExitAnalyticsEvent = useCallback(
		() =>
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'adminKeyExit',
					source: 'superAdminBanner',
				},
			}).fire(),
		[createAnalyticsEvent],
	);

	const fireAdminKeyExitFailedAnalyticsEvent = useCallback(
		() =>
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'failed',
					actionSubject: 'exitAdminKeySession',
					source: 'superAdminBanner',
				},
			}).fire(),
		[createAnalyticsEvent],
	);

	const deleteStoredRestrictedPage = () => localStorage.removeItem(keys.RESTRICTED_PAGE_CONFIRMED);

	const handleMutationCompleteCallback = useCallback(() => {
		showSurvey(false);
		deleteStoredRestrictedPage();
		fireAdminKeyExitAnalyticsEvent();
	}, [fireAdminKeyExitAnalyticsEvent]);

	const handleMutationErrorCallback = () => {
		void Promise.resolve(fireAdminKeyExitFailedAnalyticsEvent());
	};

	const renderBanner = () => {
		// This constant is adapted from the <AKBanner> source code as our custom <Banner> is replacing
		// an <AkBanner>.  This code adds the accesibility attributes of the <AkBanner> so screen
		// readers work the same with the custom banner.
		const accessibilityProps = {
			role: 'alert',
		};

		if (bannerState.getTotalHeight() !== BANNER_TOTAL_HEIGHT) {
			setBannerHeight(BANNER_TOTAL_HEIGHT);
			bannerState.show(BANNER_NAME, bannerHeight);
			addUFOCustomData({
				isSuperAdminBannerDisplayed: true,
			});
		}

		return (
			<DisableSuperAdminMutation
				handleMutationCompleteCallback={handleMutationCompleteCallback}
				handleMutationErrorCallback={handleMutationErrorCallback}
			>
				{(disableSuperAdmin) => (
					<Box xcss={bannerWrapperStyles} data-vc="super-admin-banner" {...ssrPlaceholderIdProp}>
						<Banner {...accessibilityProps}>
							{WARNING_ICON}
							<BannerText>
								<FormattedMessage
									id="super-admin.banner.message"
									description="Message in the banner to notify users of super admin mode"
									defaultMessage="You’re using admin key, which gives you temporary access to restricted content."
								/>
								<ExitLink onClick={disableSuperAdmin}>
									<FormattedMessage
										id="super-admin.banner.exit"
										description="Click to action in banner to allow users to exit super admin mode"
										defaultMessage="Stop using admin key"
									/>
								</ExitLink>
							</BannerText>
						</Banner>
					</Box>
				)}
			</DisableSuperAdminMutation>
		);
	};

	/**
	 * We are using the BannerStateContainer for storing which banners are visible, so we also have
	 * to tell BannerStateContainer when our banner isn't visible any more.  Also, note that this
	 * method is intended to be called during render such that it is expected to return valid JSX,
	 * which is why it must return null.
	 */
	const hideBanner = () => {
		bannerState.hide(BANNER_NAME);
		return null;
	};

	const getBannerDisplay = (loading, error, isSuperAdmin) => {
		if (error) {
			return <ErrorDisplay error={error} />;
		} else if (loading) {
			return null;
		}
		return isSuperAdmin ? renderBanner() : hideBanner();
	};

	const renderBannerQuery = () =>
		getSuperAdminClaim() && (
			<AdminKeyQuery>
				{({ loading, error, isSuperAdmin }) => getBannerDisplay(loading, error, isSuperAdmin)}
			</AdminKeyQuery>
		);

	return (
		<>
			{renderBannerQuery()}
			{isSurveyVisible && <SuperAdminSatisfactionSurveyLoader onDone={hideSurvey} />}
			<SuperAdminExpiration />
		</>
	);
};

export const SuperAdminBanner = () => (
	<Subscribe to={[BannerStateContainer]}>
		{(bannerState: BannerStateContainer) => <SuperAdminBannerComponent bannerState={bannerState} />}
	</Subscribe>
);
